import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EqualizerIcon from '@mui/icons-material/Equalizer'
import AirplaneTicketIcon from '@mui/icons-material/AirplaneTicket'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person';
import { Badge, BadgeProps, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AuthContext from '../../context/auth';
import { useNavigate } from 'react-router-dom';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import useAxios from 'axios-hooks'
import moment from 'moment';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PhoneIcon from '@mui/icons-material/Phone';
import TelegramIcon from '@mui/icons-material/Telegram';
import SosIcon from '@mui/icons-material/Sos';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { APP_NAME, APP_CONTACTS, V2_ADMIN_URL } from '@next-pontos/settings';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { V2InviteModal } from '../V2InviteModal';
import ShareIcon from '@mui/icons-material/Share';
import { Loader } from '../Loader'
import StorefrontIcon from '@mui/icons-material/Storefront';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { AdminPanelSettings, SvgIconComponent } from '@mui/icons-material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { DisplayAppVersion } from '../DisplayAppVersion';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PaidIcon from '@mui/icons-material/Paid';
import { NotificationBell } from '../NotificationBell';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

type LayoutMenuItem = {
  label: string,
  tooltip: string,
  Icon: SvgIconComponent,
  badgeProps?: BadgeProps,
  onClick?: () => void
  href: string, 
  target?: string
}

type PendingOrderLike = {
  confirmation?: string
  created_at?: string | Date
}

type PaymentLike = {
  status?: string
}

const overduePendingCutoff = () => moment().subtract(48, 'hours')

const isOverduePendingOrder = (item: PendingOrderLike) =>
  item?.confirmation === 'PENDING' && moment(item.created_at).isBefore(overduePendingCutoff())

const asList = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object' && Array.isArray((data as { rows?: unknown }).rows)) {
    return (data as { rows: T[] }).rows
  }
  return []
}

const hasOverduePendingOrders = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false
  if (typeof (data as { total?: unknown }).total === 'number') {
    return ((data as { total: number }).total ?? 0) > 0
  }
  return asList<PendingOrderLike>(data).some(isOverduePendingOrder)
}

const hasLatePayments = (data: unknown): boolean =>
  asList<PaymentLike>(data).some((item) => item?.status === 'LATE')

const errorBadge = { color: 'error' as const, badgeContent: '!' }

export function Layout({children}: {children: React.ReactNode}) {
  const theme = useTheme()
  const isBigScreen = useMediaQuery(theme.breakpoints.up('md'))
  const isBiggerThanSm = useMediaQuery(theme.breakpoints.up('sm'))
  const { me, signOut } = React.useContext(AuthContext)
  
  const [open, setOpen] = React.useState(isBiggerThanSm && me?.role === 'USER');
  const [drawerVariant, setDrawerVariant] = React.useState<'temporary' | 'permanent'>(isBigScreen ? "permanent" : "temporary");
  
  const navigate = useNavigate()

  const [payments, getPayments] = useAxios('/payment', {
    manual: true,
    autoCancel: false
  })

  // Only fetches a single row + total to know if there's any pending order
  // older than 48h, used to drive the "Emissões" badge below.
  const overdueOrdersFilter = React.useMemo(() => ({
    items: [
      { field: 'confirmation', operator: 'is', value: 'PENDING' },
      {
        field: 'created_at',
        operator: 'before',
        value: moment().subtract(48, 'hours').toISOString(),
      },
    ],
  }), [])

  const overdueOrdersUrl = React.useMemo(() => {
    const params = new URLSearchParams({
      page: '0',
      pageSize: '1',
      filter: JSON.stringify(overdueOrdersFilter),
    })
    return `/order?${params.toString()}`
  }, [overdueOrdersFilter])

  const [orders, getOrders] = useAxios(overdueOrdersUrl, {
    manual: true,
    autoCancel: false
  })

  const handleDrawerOpen = () => {
    setOpen(true);
    
    if (!isBigScreen) {
      setDrawerVariant('permanent')
    }
    
  };

  const handleDrawerClose = () => {
    setOpen(false);

    if (!isBigScreen) {
      setDrawerVariant('temporary')
    }    
  };

  const onLogoutClick = React.useCallback(() => {
    signOut(() => {
      window.location.href = '/login'
    })
  }, [signOut, navigate])
  
  const activeListItemStyles = React.useCallback((url: string) => {
    const blocks = window.location.pathname.split('/')

    let adminBasePathName = blocks[2] ? `/${blocks[1]}/${blocks[2]}` : `/${blocks[1]}`
    let basePathName = `/${blocks[1]}`

    if (url.includes('/admin')) {
      if (url === '/admin') {
        return url === adminBasePathName ? {
          color: theme.palette.primary.main
        } : {}
      } else {
        if (url.includes(adminBasePathName) && adminBasePathName !== '/admin') {
          return {
            color: theme.palette.primary.main
          }
        }
      }

      return {}
    } else {
      return url.includes(basePathName) ? {
        color: theme.palette.primary.main
      }: {} 
    }
    
  }, [theme])

  React.useEffect(() => {
    if (me) {
      if (me.is_first_login) {
        window.location.href = '/bem-vindo'
      }
    }
  }, [me])

  React.useEffect(() => {
    if (me?.role === 'ADMIN' && !payments.loading) {
      getPayments()
      getOrders()
    }
  }, [me?.role])

  const userMenuItems: LayoutMenuItem[] = React.useMemo(() => {
    const menuItems: LayoutMenuItem[] = [
      { label: 'Home', tooltip: 'Home', Icon: EqualizerIcon, href: '/home' },
      { label: 'Minhas Ofertas', tooltip: 'Minha Ofertas', Icon: ChecklistRtlIcon, href: '/minhas-ofertas' },
      { label: 'Meus Pagamentos', tooltip: 'Meus Pagamentos', Icon: PaymentsIcon, href: '/meus-pagamentos' },
      { label: 'Minhas Contas', tooltip: 'Minhas Contas', Icon: PeopleIcon, href: '/minhas-contas' },
    ]

    if (me?.user_module?.is_referral_enabled) {
      menuItems.push({ label: 'Minhas Indicações', tooltip: 'Minhas Indicações', Icon: ShareIcon, href: '/minhas-indicacoes' })
    }

    menuItems.push({ label: 'Meu Perfil', tooltip: 'Meu Perfil', Icon: PersonIcon, href: '/meu-perfil', badgeProps: !me?.pix_key ? { color: 'error', badgeContent: '!' } : undefined })

    return menuItems
  }, [me?.pix_key, me?.user_module?.is_referral_enabled])

  const adminMenuItems: LayoutMenuItem[] = React.useMemo(() => {
    const menuItems: LayoutMenuItem[] = []

    if (me?.role === 'ADMIN') {
      menuItems.push({ label: 'Dashboard', tooltip: 'Dashboard', Icon: EqualizerIcon, href: '/admin' })
      menuItems.push({ label: 'Usuários', tooltip: 'Usuário', Icon: PeopleIcon, href: '/admin/usuarios'})
      menuItems.push({ label: 'Departamentos', tooltip: 'Departamentos', Icon: AssignmentIndIcon, href: '/admin/departamentos'})
    }

    if (me?.role === 'ADMIN' || me?.role === 'SELLER' || me?.role === 'AGENCY') {
      menuItems.push({ label: 'Ofertas', tooltip: 'Ofertas', Icon: ChecklistRtlIcon, href: '/admin/ofertas' })

      menuItems.push({ 
        label: 'Emissões', 
        tooltip: 'Emissões', 
        Icon: AirplaneTicketIcon, 
        href:'/admin/emissoes', 
        badgeProps: hasOverduePendingOrders(orders?.data) ? errorBadge : undefined 
      })

      if (me.role !== 'AGENCY') {
        menuItems.push({ label: 'Comissões', tooltip: 'Comissões', Icon: PaidIcon, href: '/admin/comissoes' })
      }

      menuItems.push({ label: 'Clientes', tooltip: 'Clientes', Icon: HandshakeIcon, href: '/admin/clientes' })
    }

    if (me?.role === 'ADMIN') {
      menuItems.push({ label: 'Indicações', tooltip: 'Indicações', Icon: ShareIcon, href: '/admin/indicacoes' })

      menuItems.push({
        label: 'Financeiro',
        tooltip: 'Financeiro',
        Icon: AccountBalanceIcon,
        href: '/admin/financeiro',
        badgeProps: hasLatePayments(payments?.data) ? errorBadge : undefined
      })

      menuItems.push({ label: 'Agências', tooltip: 'Agência', Icon: StorefrontIcon, href: '/admin/agencias'})
      menuItems.push({ label: 'Leads', tooltip: 'Leads', Icon: AdsClickIcon, href: '/admin/leads' })
      menuItems.push({ label: 'Cupons', tooltip: 'Cupons', Icon: LocalOfferIcon, href: '/admin/cupons' })
      menuItems.push({ label: 'Cartões', tooltip: 'Cartões', Icon: CreditCardIcon, href: '/admin/cartoes' })
      menuItems.push({ label: 'Programas', tooltip: 'Programas', Icon: LoyaltyIcon, href: '/admin/programas' })
    }

    if (me?.role === 'ADMIN' || me?.role === 'SELLER') {
      menuItems.push({ label: 'Chamados', tooltip: 'Chamados', Icon: SosIcon, href: '/admin/chamados' })
    }

    return menuItems
  }, [me?.role, orders?.data, payments?.data])

  if (!me) {
    return (
      <Loader />
    )
  }
  return (
  <React.Fragment>
    {window.location.pathname.startsWith('/admin') && ['ADMIN', 'SELLER', 'AGENCY'].includes(me?.role) && (
      <V2InviteModal />
    )}
    <Box className="flex">
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: 'none' }),
              }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" className="flex flex-1">
            Bem-vindo(a) à {APP_NAME}, {me?.name}!
          </Typography>
          <NotificationBell />
          <div className="flex items-end flex-col">
            <Typography variant="caption" noWrap component="div" className="flex">
              <PhoneIcon fontSize="small" className="mr-2" /> {`+${APP_CONTACTS.whatsapp.slice(0, 2)} ${APP_CONTACTS.whatsapp.slice(2, 4)} ${APP_CONTACTS.whatsapp.slice(4, 9)}-${APP_CONTACTS.whatsapp.slice(9, 13)}`}
            </Typography>
            <Typography variant="caption" noWrap component="div" className="flex">
              <TelegramIcon fontSize="small" className="mr-2" /> {`@${APP_CONTACTS.telegram}`}
            </Typography>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer variant={drawerVariant} open={open}>
        <DrawerHeader>
          <div className="flex flex-1 justify-center items-center">
            <img alt="logo" src="/logo.png" height={120} width={120}/>
          </div>
          {open && (
            <IconButton onClick={handleDrawerClose}>
              {!open ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
        </DrawerHeader>
        {!window.location.pathname.startsWith('/admin') && (
          <React.Fragment>
            <Divider />
            <List>
              {userMenuItems.map((menuItem) => {
                const Icon = menuItem.Icon;
                return (
                  <ListItem disablePadding className="block">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...(menuItem.href ? activeListItemStyles(menuItem.href) : {})
                      }}
                      href={menuItem.href}
                    >
                      <Tooltip title={menuItem.tooltip}>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        {
                          menuItem.badgeProps ? (
                            <Badge {...menuItem.badgeProps}>
                              <Icon style={activeListItemStyles(menuItem.href)} />
                            </Badge>
                          ) : (
                            <Icon style={activeListItemStyles(menuItem.href)} />
                          )}
                        </ListItemIcon>
                      </Tooltip>
                      <ListItemText primary={menuItem.label} className={`${open ? 'opacity-100' : 'opacity-0'}`} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            {['ADMIN', 'SELLER', 'AGENCY'].includes(me?.role) && (
              <React.Fragment>
                <Divider />
                <List>
              <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                  href='/admin'
                  target='_blank'
                    >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                        <AdminPanelSettings />
                      </ListItemIcon>
                  <ListItemText primary={'Painel Administrativo'} sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>
                </List>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {window.location.pathname.startsWith('/admin') && (
          <React.Fragment>
            <Divider />
            {(me?.role === 'ADMIN' || me?.role === 'SELLER' || me.role === 'AGENCY') && (
              <React.Fragment>
                <List>
                  {adminMenuItems.map((menuItem) => {
                    const Icon = menuItem.Icon;
                    return (
                      <ListItem disablePadding className="block">
                        <ListItemButton
                              sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                                ...(menuItem.href ? activeListItemStyles(menuItem.href) : {})
                              }}
                          href={menuItem.href}
                        >
                          <Tooltip title={menuItem.tooltip}>
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: open ? 3 : 'auto',
                                  justifyContent: 'center',
                                }}
                              >
                                {
                                  menuItem.badgeProps ? (
                                <Badge {...menuItem.badgeProps}>
                                  <Icon style={activeListItemStyles(menuItem.href)} />
                                </Badge>
                              ) : (
                                <Icon style={activeListItemStyles(menuItem.href)} />
                              )}
                            </ListItemIcon>
                          </Tooltip>
                          <ListItemText primary={menuItem.label} className={`${open ? 'opacity-100' : 'opacity-0'}`} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
                {!!V2_ADMIN_URL && (
                  <React.Fragment>
                    <Divider />
                    <List>
                      <ListItem disablePadding className="block">
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 2.5,
                            color: theme.palette.primary.main,
                          }}
                          href={V2_ADMIN_URL}
                        >
                          <Tooltip title="Ir para a nova versão (V2)">
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: open ? 3 : 'auto',
                                justifyContent: 'center',
                                color: theme.palette.primary.main,
                              }}
                            >
                              <RocketLaunchIcon />
                            </ListItemIcon>
                          </Tooltip>
                          <ListItemText
                            primary="Ir para a V2"
                            primaryTypographyProps={{ fontWeight: 'bold' }}
                            className={`${open ? 'opacity-100' : 'opacity-0'}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        <Divider />
        <List className="flex flex-1 flex-col">
          {APP_CONTACTS.feedback_form_link && (
            <ListItem disablePadding className="block">
              <ListItemButton
                className="min-h-12 justify-start px-2.5"
                href={APP_CONTACTS.feedback_form_link}
                target="_blank"
              >

              <Tooltip title='Feedback'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                    <FeedbackIcon />
                  </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Feedback' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding className="block">
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/ajuda')
              }}
              href={'/ajuda'}
            >
              <Tooltip title='Ajuda'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <QuestionAnswerIcon style={activeListItemStyles('/ajuda')} />
                </ListItemIcon>
              </Tooltip>
              <ListItemText primary="Ajuda" className={`${open ? 'opacity-100' : 'opacity-0'}`} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding className="block">
            <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              onClick={onLogoutClick}
            >
              <Tooltip title='Logout'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon></Tooltip>
                <ListItemText primary='Logout' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <div className="flex flex-1 flex-col justify-end items-center gap-2 relative">
            <img alt="logo" src="/logo-square.png" height={50} width={50}/>
            <DisplayAppVersion />
          </div>
        </List>
      </Drawer>
      <Box component="main" className="w-full h-[calc(100vh-64px)]">
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  </React.Fragment>
  );
}
